// THE BUILDING KUBES' FACES (building-kubes.mjs; the owner's building-kubes proposal, confirmed Oct 13).
//
// Each style is ONE grey canvas, shared by every colour of its idea and by its bit: TextureEngine sets the material's
// colour to the kube's own hex (its material's: a Mineral's, a Regolith's, a wood's, a metal's) and the GPU multiplies
// this canvas by it, the crust's and the bioliths' way. So the shared-slot rules hold: drawn in greys from a FIXED seed,
// with no colour argument and no reading of anybody's hex (every maker takes no parameters), with fills and strokes
// alone (no per-pixel pass, tests/material-looks.test.mjs), the same canvas in every session.
//
// TWO COLOURS FROM ONE GREY. The kubes the proposal painted in two colours are drawn so the multiply makes the second:
//   * HAZARD STRIPES are white stripes on near-black, so the stripes come out the Mineral's colour and the rest black
//     (the owner: a stripe colour for every Mineral bit);
//   * the LANTERN, the GLOW STRIP PANEL and the COR INLAID PANEL are a dark frame round a bright lamp, band or inlay,
//     tinted by the light's colour, and the renderer's glow (system-view.js, fromColor) lights each texel by its own
//     brightness, so the bright part glows and the frame stays dark, as a beacon's tissue does.
// The STEEL GRATE is the one that uses ALPHA: bars on a transparent canvas, drawn as a pane is (isGlassKubeName), with
// the holes cut out (alphaTest) so you see through it.
//
// EDGES. Every pattern that repeats is laid out on the canvas's period, and anything that crosses an edge is drawn again
// where it comes back in, so a wall of these kubes reads as one wall. The PANEL faces (the Vent Grille, the Lantern, the
// Cor Inlaid Panel, the Cut Crystal's facets) are one motif per face by design, and so are the four PILLAR canvases,
// which the renderer stands upright on each side face of a column (building-kubes.mjs pillarUv).
//
// THREE-free and DOM-light (it asks the document for a canvas and nothing else), so a test can record what it draws.

export const BUILD_TEX_SIZE = 1024;
const S = BUILD_TEX_SIZE;

function rng(seed) { let x = seed >>> 0; return () => ((x = (Math.imul(x, 1664525) + 1013904223) >>> 0) / 4294967296); }
const grey = (v) => { const c = Math.max(0, Math.min(255, Math.round(v * 255))); return `rgb(${c}, ${c}, ${c})`; };
const ink = (a) => `rgba(0, 0, 0, ${Math.max(0, Math.min(1, a)).toFixed(3)})`;
const chalk = (a) => `rgba(255, 255, 255, ${Math.max(0, Math.min(1, a)).toFixed(3)})`;
function canvasOf(fill) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (fill != null) { ctx.fillStyle = grey(fill); ctx.fillRect(0, 0, S, S); }
  return { canvas, ctx };
}
// The offsets a shape with box [x0, x1] × [y0, y1] is drawn at so the canvas tiles: itself, and once more for every edge
// it crosses.
function offsets(x0, y0, x1, y1) {
  const out = [];
  for (const dx of [-S, 0, S]) for (const dy of [-S, 0, S])
    if (x1 + dx > 0 && x0 + dx < S && y1 + dy > 0 && y0 + dy < S) out.push([dx, dy]);
  return out;
}
const mod = (v) => ((v % S) + S) % S;

// BLOCKS: rectangles on the canvas's period, each its own grey, with a lit top edge and a shaded foot.
function blocks(ctx, rects, R, o) {
  const g = o.gap / 2, bevel = o.bevel == null ? 6 : o.bevel;
  for (const [rx, ry, w, h] of rects) {
    const x = mod(rx), y = mod(ry), v = o.base + (R() - 0.5) * o.vary;
    for (const [dx, dy] of offsets(x, y, x + w, y + h)) {
      const X = x + dx + g, Y = y + dy + g, W = w - o.gap, H = h - o.gap;
      ctx.fillStyle = grey(v); ctx.fillRect(X, Y, W, H);
      if (bevel) {
        ctx.fillStyle = chalk(0.12); ctx.fillRect(X, Y, W, bevel);
        ctx.fillStyle = ink(0.07); ctx.fillRect(X, Y + H - bevel, W, bevel);
      }
    }
  }
}
// SPECKS: the grain, tiny squares of a little black or white, wrapped.
function specks(ctx, R, n, a, size = 1) {
  for (let i = 0; i < n; i++) {
    const dark = R() < 0.5, r = (2.4 + R() * 5.6) * size, x = R() * S, y = R() * S;
    ctx.fillStyle = dark ? ink(a) : chalk(a);
    for (const [dx, dy] of offsets(x, y, x + r, y + r)) ctx.fillRect(x + dx, y + dy, r, r);
  }
}
// CLOUDS: soft round shading, each fading to its own colour at no alpha, wrapped.
function clouds(ctx, R, n, a, r0, r1) {
  for (let i = 0; i < n; i++) {
    const x = R() * S, y = R() * S, r = r0 + R() * (r1 - r0), dark = R() < 0.5;
    for (const [dx, dy] of offsets(x - r, y - r, x + r, y + r)) {
      const X = x + dx, Y = y + dy, gr = ctx.createRadialGradient(X, Y, 0, X, Y, r);
      gr.addColorStop(0, dark ? ink(a) : chalk(a)); gr.addColorStop(1, dark ? ink(0) : chalk(0));
      ctx.fillStyle = gr; ctx.fillRect(X - r, Y - r, 2 * r, 2 * r);
    }
  }
}
// A batch of short strokes in one path (one draw): tool marks, grain, frost.
function strokes(ctx, segs, width, style) {
  ctx.beginPath();
  for (const [x0, y0, x1, y1] of segs) for (const [dx, dy] of offsets(Math.min(x0, x1), Math.min(y0, y1), Math.max(x0, x1), Math.max(y0, y1))) {
    ctx.moveTo(x0 + dx, y0 + dy); ctx.lineTo(x1 + dx, y1 + dy);
  }
  ctx.lineWidth = width; ctx.strokeStyle = style; ctx.lineCap = 'round'; ctx.stroke();
}
// A FRAME round the face: a panel's border.
function frame(ctx, inset, w, v) { ctx.lineWidth = w; ctx.strokeStyle = grey(v); ctx.strokeRect(inset, inset, S - 2 * inset, S - 2 * inset); }

// ── CHISELLED MINERALS ────────────────────────────────────────────────────────────────────────────────────────────────
// CHISELLED BRICKS: three courses a kube, two blocks a course, each course a third of a block on from the last (a raking
// bond, so the fourth course, the next kube's first, lines up again), with the tool marks still on them.
export function drawBuildChisel() {
  const { canvas, ctx } = canvasOf(0.6), R = rng(0xbd0001), h = S / 3, w = S / 2, r = [];
  for (let row = 0; row < 3; row++) for (let i = 0; i < 2; i++) r.push([i * w + row * (w / 3), row * h, w, h]);
  blocks(ctx, r, R, { gap: 12, base: 0.9, vary: 0.07, bevel: 8 });
  const marks = [[], []];
  for (let i = 0; i < 520; i++) { const x = R() * S, y = R() * S, L = 14 + R() * 18; marks[i & 1].push([x, y, x + L, y + L * 0.5]); }
  strokes(ctx, marks[0], 3.5, ink(0.1)); strokes(ctx, marks[1], 2.5, chalk(0.08));
  specks(ctx, R, 500, 0.05);
  return canvas;
}

// THE PILLAR: eight flutes, lit on one side, and the mouldings of a base and a capital. Four canvases, one for each part
// the renderer draws (building-kubes.mjs pillarRole): the short column whole ('pillar', the icon too), its base, its
// shaft and its capital. Their END bands (the plinth's bottom, the abacus's top) are plain, because an end face (the top
// of a capital) samples them (PILLAR_END_BAND).
const FLUTE = S / 8;
function flutes(ctx, y0, y1, inset) {
  for (let i = 0; i < 8; i++) {
    const x = i * FLUTE, g = ctx.createLinearGradient(x, 0, x + FLUTE, 0);
    g.addColorStop(0, grey(0.7)); g.addColorStop(0.3, grey(0.97)); g.addColorStop(0.75, grey(0.88)); g.addColorStop(1, grey(0.66));
    ctx.fillStyle = g; ctx.fillRect(x + inset, y0, FLUTE - 2 * inset, y1 - y0);
  }
}
function band(ctx, y, h, top, mid, bottom) {   // a round moulding: a horizontal band lit from above
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, grey(top)); g.addColorStop(0.45, grey(mid)); g.addColorStop(1, grey(bottom));
  ctx.fillStyle = g; ctx.fillRect(0, y, S, h);
}
function slab(ctx, y, h, v) { ctx.fillStyle = grey(v); ctx.fillRect(0, y, S, h); ctx.fillStyle = chalk(0.14); ctx.fillRect(0, y, S, 6); ctx.fillStyle = ink(0.08); ctx.fillRect(0, y + h - 6, S, 6); }
const BASE_TOP = 760, CAP_FOOT = 264;           // where the flutes stop above a base and below a capital
function baseOf(ctx) { band(ctx, BASE_TOP, 70, 0.98, 0.84, 0.62); band(ctx, BASE_TOP + 70, 104, 0.96, 0.86, 0.6); slab(ctx, 934, 90, 0.86); }
function capOf(ctx) { slab(ctx, 0, 84, 0.88); band(ctx, 84, 110, 0.95, 0.82, 0.6); band(ctx, 194, 40, 0.9, 0.78, 0.64); ctx.fillStyle = grey(0.62); ctx.fillRect(0, CAP_FOOT - 30, S, 30); }
export function drawBuildPillarShaft() {
  const { canvas, ctx } = canvasOf(0.6), R = rng(0xbd0012);
  flutes(ctx, 0, S, 0); specks(ctx, R, 500, 0.04);
  return canvas;
}
export function drawBuildPillarBase() {
  const { canvas, ctx } = canvasOf(0.6), R = rng(0xbd0011);
  flutes(ctx, 0, BASE_TOP, 6); baseOf(ctx); specks(ctx, R, 500, 0.04);
  return canvas;
}
export function drawBuildPillarCap() {
  const { canvas, ctx } = canvasOf(0.6), R = rng(0xbd0013);
  flutes(ctx, CAP_FOOT, S, 6); capOf(ctx); specks(ctx, R, 500, 0.04);
  return canvas;
}
export function drawBuildPillar() {               // the short column: a capital and a base with a stretch of flutes between
  const { canvas, ctx } = canvasOf(0.6), R = rng(0xbd0010);
  flutes(ctx, CAP_FOOT, BASE_TOP, 6); capOf(ctx); baseOf(ctx); specks(ctx, R, 500, 0.04);
  return canvas;
}
// BUFFED MINERAL: one smooth face, soft clouding and a few long pale veins.
export function drawBuildBuffed() {
  const { canvas, ctx } = canvasOf(0.9), R = rng(0xbd0003);
  clouds(ctx, R, 30, 0.07, 90, 260);
  for (let i = 0; i < 9; i++) {
    const y = R() * S, a = 0.05 + R() * 0.06, c1 = (R() - 0.5) * 260, c2 = (R() - 0.5) * 260;
    for (const dy of [-S, 0, S]) {
      ctx.beginPath(); ctx.moveTo(0, y + dy); ctx.bezierCurveTo(S / 3, y + dy + c1, (2 * S) / 3, y + dy + c2, S, y + dy);
      ctx.lineWidth = 3 + R() * 5; ctx.strokeStyle = chalk(a); ctx.stroke();
    }
  }
  specks(ctx, R, 300, 0.03);
  return canvas;
}
// LARGE MINERAL TILES: four big squares a face, two by two, with hairline joints.
export function drawBuildTiles() {
  const { canvas, ctx } = canvasOf(0.62), R = rng(0xbd0004), r = [];
  for (let i = 0; i < 2; i++) for (let k = 0; k < 2; k++) r.push([i * 512, k * 512, 512, 512]);
  blocks(ctx, r, R, { gap: 6, base: 0.91, vary: 0.04, bevel: 4 });
  clouds(ctx, R, 16, 0.05, 80, 200); specks(ctx, R, 400, 0.035);
  return canvas;
}
// DRESSED MINERAL: a plain block with fine level tooling, and two scored lines on the half-kube (so a wall's lines fall
// every half kube, whatever its height).
export function drawBuildDressed() {
  const { canvas, ctx } = canvasOf(0.9), R = rng(0xbd0005);
  for (let y = 0; y < S; y += 16) { ctx.fillStyle = ink(0.03 + R() * 0.07); ctx.fillRect(0, y, S, 6); }
  for (const y of [256, 768]) { ctx.fillStyle = grey(0.6); ctx.fillRect(0, y - 7, S, 14); ctx.fillStyle = chalk(0.2); ctx.fillRect(0, y + 7, S, 4); }
  specks(ctx, R, 500, 0.04);
  return canvas;
}

// ── GROUND INTO BUILDING ──────────────────────────────────────────────────────────────────────────────────────────────
// RAMMED REGOLITH: level layers of different shades, as many as fill the kube exactly (so they run on up a wall), packed
// with grit.
export function drawBuildRammed() {
  const { canvas, ctx } = canvasOf(0.86), R = rng(0xbd0006), hs = [];
  let t = 0; while (t < S) { const h = 40 + R() * 120; hs.push(h); t += h; }
  const k = S / t; let y = 0;
  for (const h of hs) { ctx.fillStyle = grey(0.8 + R() * 0.16); ctx.fillRect(0, y, S, h * k); ctx.fillStyle = ink(0.06); ctx.fillRect(0, y, S, 3); y += h * k; }
  specks(ctx, R, 1400, 0.09, 0.6);
  return canvas;
}
// SINTERED REGOLITH: a glassy, knobbly block: lit nodules fused together.
export function drawBuildSintered() {
  const { canvas, ctx } = canvasOf(0.7), R = rng(0xbd0007);
  for (let i = 0; i < 170; i++) {
    const x = R() * S, y = R() * S, r = 18 + R() * 46;
    for (const [dx, dy] of offsets(x - r, y - r, x + r, y + r)) {
      const X = x + dx, Y = y + dy, g = ctx.createRadialGradient(X - r * 0.3, Y - r * 0.3, 2, X, Y, r);
      g.addColorStop(0, grey(1)); g.addColorStop(0.7, grey(0.86)); g.addColorStop(1, grey(0.68));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(X, Y, r, 0, Math.PI * 2); ctx.fill();
    }
  }
  return canvas;
}
// REGOLITH CONCRETE: smooth cast faces with the board marks of the formwork every eighth of a kube and its tie holes on a
// half-kube grid.
export function drawBuildConcrete() {
  const { canvas, ctx } = canvasOf(0.9), R = rng(0xbd0008);
  clouds(ctx, R, 24, 0.05, 90, 240);
  for (let y = 0; y < S; y += 128) { ctx.fillStyle = ink(0.07); ctx.fillRect(0, y, S, 5); ctx.fillStyle = chalk(0.08); ctx.fillRect(0, y + 5, S, 3); }
  for (const x of [256, 768]) for (const y of [256, 768]) {
    ctx.fillStyle = grey(0.55); ctx.beginPath(); ctx.arc(x, y, 22, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 6; ctx.strokeStyle = chalk(0.25); ctx.beginPath(); ctx.arc(x, y, 30, 0, Math.PI * 2); ctx.stroke();
  }
  specks(ctx, R, 900, 0.045);
  return canvas;
}

// ── WOOD ──────────────────────────────────────────────────────────────────────────────────────────────────────────────
// WOOD PANELS: five rows of boards, each row's joint somewhere of its own, with the grain running along them.
export function drawBuildPanels() {
  const { canvas, ctx } = canvasOf(0.5), R = rng(0xbd0009), h = S / 5, r = [];
  for (let row = 0; row < 5; row++) { const o = R() * S; r.push([o, row * h, S, h]); }
  blocks(ctx, r, R, { gap: 10, base: 0.88, vary: 0.1, bevel: 5 });
  for (let pass = 0; pass < 4; pass++) {
    ctx.beginPath();
    for (let i = 0; i < 40; i++) {
      const y = R() * S, c1 = (R() - 0.5) * 22, c2 = (R() - 0.5) * 22;
      for (const dy of [-S, 0, S]) { ctx.moveTo(0, y + dy); ctx.bezierCurveTo(S / 3, y + dy + c1, (2 * S) / 3, y + dy + c2, S, y + dy); }
    }
    ctx.lineWidth = 2 + pass; ctx.strokeStyle = ink(0.05 + pass * 0.02); ctx.stroke();
  }
  return canvas;
}
// WOOD SLATS: eight angled louvres a kube, each lit at the top and shaded under, with a dark gap between.
export function drawBuildSlats() {
  const { canvas, ctx } = canvasOf(0.22), R = rng(0xbd000a);
  for (let i = 0; i < 8; i++) {
    const y = i * 128, g = ctx.createLinearGradient(0, y + 8, 0, y + 112);
    g.addColorStop(0, grey(0.98)); g.addColorStop(1, grey(0.66));
    ctx.fillStyle = g; ctx.fillRect(0, y + 8, S, 104);
  }
  const grain = [];
  for (let i = 0; i < 200; i++) { const x = R() * S, y = Math.floor(R() * 8) * 128 + 14 + R() * 90; grain.push([x, y, x + 40 + R() * 80, y]); }
  strokes(ctx, grain, 2, ink(0.07));
  return canvas;
}

// ── METAL ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// TREAD PLATE: raised bars in a diamond pattern, sixteen a side, alternate ones turned a quarter.
export function drawBuildTread() {
  const { canvas, ctx } = canvasOf(0.74), R = rng(0xbd000b), u = S / 16;
  clouds(ctx, R, 10, 0.04, 120, 260);
  for (let i = 0; i < 16; i++) for (let k = 0; k < 16; k++) {
    ctx.save(); ctx.translate(i * u + u / 2, k * u + u / 2); ctx.rotate((i + k) % 2 ? Math.PI / 4 : -Math.PI / 4);
    ctx.fillStyle = ink(0.22); ctx.fillRect(-22, 2, 44, 10);
    ctx.fillStyle = grey(0.97); ctx.fillRect(-22, -6, 44, 12);
    ctx.restore();
  }
  return canvas;
}
// STEEL GRATE: bars on a transparent canvas, eight upright and four across, so the holes are seen through.
export function drawBuildGrate() {
  const { canvas, ctx } = canvasOf(null);
  ctx.clearRect(0, 0, S, S);
  for (let i = 0; i < 8; i++) {                            // upright bars centred on the period lines, wrapped
    const x0 = i * 128 - 18;
    for (const [dx] of offsets(x0, 0, x0 + 36, S)) {
      const x = x0 + dx;
      ctx.fillStyle = grey(0.86); ctx.fillRect(x, 0, 36, S);
      ctx.fillStyle = chalk(0.3); ctx.fillRect(x, 0, 8, S);
      ctx.fillStyle = ink(0.25); ctx.fillRect(x + 28, 0, 8, S);
    }
  }
  for (let k = 0; k < 4; k++) {                            // and four across, a quarter kube apart
    const y0 = k * 256 - 22;
    for (const [, dy] of offsets(0, y0, S, y0 + 44)) {
      const y = y0 + dy;
      ctx.fillStyle = grey(0.8); ctx.fillRect(0, y, S, 44);
      ctx.fillStyle = chalk(0.25); ctx.fillRect(0, y, S, 8);
      ctx.fillStyle = ink(0.25); ctx.fillRect(0, y + 36, S, 8);
    }
  }
  return canvas;
}
// CORRUGATED ALUMINUM: sixteen ribs a kube, each lit on its crest.
export function drawBuildCorrugated() {
  const { canvas, ctx } = canvasOf(0.8), R = rng(0xbd000d), w = S / 16;
  for (let i = 0; i < 16; i++) {
    const x = i * w, g = ctx.createLinearGradient(x, 0, x + w, 0);
    g.addColorStop(0, grey(0.66)); g.addColorStop(0.25, grey(0.86)); g.addColorStop(0.5, grey(0.99)); g.addColorStop(0.75, grey(0.84)); g.addColorStop(1, grey(0.66));
    ctx.fillStyle = g; ctx.fillRect(x, 0, w, S);
  }
  clouds(ctx, R, 10, 0.05, 120, 280);
  return canvas;
}
// HAZARD STRIPES: white diagonals on near-black, a quarter kube apart, so the multiply makes them the Mineral's colour
// on black; the diagonal's period divides the kube, so the stripes run on across every edge. Worn a little.
export const HAZARD_BLACK = 0.04;
export function drawBuildHazard() {
  const { canvas, ctx } = canvasOf(HAZARD_BLACK), R = rng(0xbd000e);
  ctx.fillStyle = grey(1);
  for (let i = -5; i < 5; i++) {
    const x = i * 256; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + 128, 0); ctx.lineTo(x + 128 + S, S); ctx.lineTo(x + S, S); ctx.closePath(); ctx.fill();
  }
  specks(ctx, R, 700, 0.1);
  return canvas;
}
// VENT GRILLE: a framed panel of nine dark slots, ONE ON EACH FACE.
export function drawBuildVent() {
  const { canvas, ctx } = canvasOf(0.8), R = rng(0xbd000f);
  frame(ctx, 52, 44, 0.6); frame(ctx, 84, 8, 0.96);
  for (let i = 0; i < 9; i++) {
    const y = 168 + i * 80;
    ctx.fillStyle = grey(0.06); ctx.fillRect(160, y, S - 320, 38);
    ctx.fillStyle = grey(0.97); ctx.fillRect(160, y + 38, S - 320, 8);
  }
  for (const [x, y] of [[100, 100], [S - 100, 100], [100, S - 100], [S - 100, S - 100]]) { ctx.fillStyle = grey(0.5); ctx.beginPath(); ctx.arc(x, y, 14, 0, Math.PI * 2); ctx.fill(); }
  specks(ctx, R, 300, 0.04);
  return canvas;
}

// ── ICE AND SNOW ──────────────────────────────────────────────────────────────────────────────────────────────────────
// ICE BRICKS: two courses of big blocks, offset by half, with pale frosted joints and a few frost lines in the ice.
export function drawBuildIcebrick() {
  const { canvas, ctx } = canvasOf(1), R = rng(0xbd0014), r = [];
  for (let row = 0; row < 2; row++) for (let i = 0; i < 2; i++) r.push([i * 512 + row * 256, row * 512, 512, 512]);
  blocks(ctx, r, R, { gap: 18, base: 0.86, vary: 0.05, bevel: 0 });
  clouds(ctx, R, 16, 0.08, 80, 200);
  const cracks = [];
  for (let k = 0; k < 16; k++) { let x = R() * S, y = R() * S; for (let s = 0; s < 4; s++) { const nx = x + (R() - 0.5) * 120, ny = y + (R() - 0.5) * 120; cracks.push([x, y, nx, ny]); x = nx; y = ny; } }
  strokes(ctx, cracks, 4, ink(0.12)); strokes(ctx, cracks, 1.5, chalk(0.4));
  return canvas;
}
// SNOW BLOCKS: four courses of packed-snow bricks, offset by half, with soft grey joints and a sparkle.
export function drawBuildSnow() {
  const { canvas, ctx } = canvasOf(0.8), R = rng(0xbd0015), r = [];
  for (let row = 0; row < 4; row++) for (let i = 0; i < 2; i++) r.push([i * 512 + (row % 2) * 256, row * 256, 512, 256]);
  blocks(ctx, r, R, { gap: 14, base: 0.96, vary: 0.04, bevel: 0 });
  specks(ctx, R, 900, 0.06, 0.7);
  return canvas;
}

// THE DARK of a lamp's frame, a panel's plate: a canvas is read as linear (NoColorSpace, as every slot is), so a grey of
// 0.16 shows as about 0.44 on screen; at 0.05 a frame reads as dark iron beside the light it holds.
const DARK = 0.05;
// ── LIGHT ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// LANTERN KUBE: a dark iron frame round a glowing window, crossed by its glazing bars, ONE ON EACH FACE.
export function drawBuildLantern() {
  const { canvas, ctx } = canvasOf(DARK), R = rng(0xbd0016), c = S / 2;
  const g = ctx.createRadialGradient(c, c, 30, c, c, 440);
  g.addColorStop(0, grey(1)); g.addColorStop(0.55, grey(0.92)); g.addColorStop(1, grey(0.5));
  ctx.fillStyle = g; ctx.fillRect(120, 120, S - 240, S - 240);
  ctx.fillStyle = grey(DARK); ctx.fillRect(c - 24, 120, 48, S - 240); ctx.fillRect(120, c - 24, S - 240, 48);
  frame(ctx, 120, 10, 0.16); frame(ctx, 40, 14, 0.18);
  for (const [x, y] of [[80, 80], [S - 80, 80], [80, S - 80], [S - 80, S - 80]]) { ctx.fillStyle = grey(0.2); ctx.beginPath(); ctx.arc(x, y, 16, 0, Math.PI * 2); ctx.fill(); }
  specks(ctx, R, 200, 0.05);
  return canvas;
}
// THE GAS LAMPS (Oct 29): a Lantern's frame round Neon or Helium, each its own texture slot (the kube's colour tints it).
export function drawBuildNeonlamp() { return drawBuildLantern(); }
export function drawBuildBalloonlamp() { return drawBuildLantern(); }
// GLOW STRIP PANEL: a dark panel with two lit bands, half a kube apart, so a corridor's strips run on along it.
export function drawBuildGlowstrip() {
  const { canvas, ctx } = canvasOf(DARK), R = rng(0xbd0017);
  for (const y of [256, 768]) {
    ctx.fillStyle = grey(0.16); ctx.fillRect(0, y - 40, S, 80);
    ctx.fillStyle = grey(0.88); ctx.fillRect(0, y - 26, S, 52);
    ctx.fillStyle = grey(1); ctx.fillRect(0, y - 9, S, 18);
  }
  for (const x of [0, 512]) for (const dx of x === 0 ? [0, S] : [0]) { ctx.fillStyle = ink(0.5); ctx.fillRect(x - 4 + dx, 0, 8, S); }
  specks(ctx, R, 300, 0.04);
  return canvas;
}

// ── STATIONS AND TECH ─────────────────────────────────────────────────────────────────────────────────────────────────
// HULL PLATING: plates of four sizes on the kube's period, their panel lines wrapped, a stencil mark and a vent strip.
export function drawBuildHull() {
  const { canvas, ctx } = canvasOf(0.82), R = rng(0xbd0018);
  clouds(ctx, R, 16, 0.05, 100, 260);
  const lines = [];
  for (const [x, y, w, h] of [[0, 0, 640, 384], [640, 0, 384, 384], [0, 384, 384, 640], [384, 384, 640, 640]]) lines.push([x, y, x + w, y], [x, y, x, y + h]);
  strokes(ctx, lines, 10, grey(0.5));
  for (let i = 0; i < 6; i++) { ctx.fillStyle = grey(0.5); ctx.fillRect(440 + i * 88, 920, 48, 24); }
  ctx.fillStyle = grey(0.97); ctx.fillRect(80, 80, 120, 24); ctx.fillRect(80, 120, 72, 24);
  specks(ctx, R, 300, 0.04);
  return canvas;
}
// HONEYCOMB PANEL: hexagonal cells of carbon, eight across and eight down, their walls lit: POLYGONS on the canvas's
// period (alternate rows offset by half a cell), never a per-pixel field.
export function drawBuildHoneycomb() {
  const { canvas, ctx } = canvasOf(0.95), R = rng(0xbd0019), w = S / 8, step = S / 8, hh = step / 0.75 / 2;
  for (let row = 0; row < 8; row++) for (let i = 0; i < 8; i++) {
    const cx = i * w + (row % 2) * (w / 2), cy = row * step, v = 0.36 + R() * 0.06;
    for (const [dx, dy] of offsets(cx - w / 2, cy - hh, cx + w / 2, cy + hh)) {
      const X = cx + dx, Y = cy + dy;
      ctx.beginPath();
      for (let k = 0; k < 6; k++) { const a = Math.PI / 6 + k * Math.PI / 3; ctx[k ? 'lineTo' : 'moveTo'](X + (w / 2) * Math.cos(a) / Math.cos(Math.PI / 6), Y + hh * Math.sin(a)); }
      ctx.closePath(); ctx.fillStyle = grey(v); ctx.fill();
      ctx.lineWidth = 12; ctx.strokeStyle = grey(0.95); ctx.stroke();
    }
  }
  return canvas;
}
// COR INLAID PANEL: dark plate with lines and nodes of Cor in it, ONE ON EACH FACE; the lines and nodes are white, so they
// come out the Cor's colour and glow.
export function drawBuildCorinlay() {
  const { canvas, ctx } = canvasOf(DARK), R = rng(0xbd001a);
  clouds(ctx, R, 10, 0.05, 120, 260);
  frame(ctx, 30, 20, 0.14);
  const path = [[0, 256, 384, 256], [384, 256, 640, 512], [640, 512, S, 512], [256, S, 256, 704], [256, 704, 512, 512], [640, 512, 768, 384], [768, 384, 768, 0]];
  strokes(ctx, path, 40, grey(0.3)); strokes(ctx, path, 18, grey(1));
  for (const [x, y] of [[384, 256], [640, 512], [256, 704], [768, 384]]) {
    ctx.fillStyle = grey(0.3); ctx.beginPath(); ctx.arc(x, y, 44, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = grey(1); ctx.beginPath(); ctx.arc(x, y, 30, 0, Math.PI * 2); ctx.fill();
  }
  return canvas;
}

// ── FROM LIFE AND RARE STONE ──────────────────────────────────────────────────────────────────────────────────────────
// CUT CRYSTAL: eight facets meeting at the face's centre, each its own shade, their edges bright, ONE ON EACH FACE.
export function drawBuildCrystal() {
  const { canvas, ctx } = canvasOf(0.7), R = rng(0xbd001b), h = S / 2;
  const pts = [[0, 0], [h, 0], [S, 0], [0, h], [h, h], [S, h], [0, S], [h, S], [S, S]];
  const tri = [[0, 1, 4], [1, 2, 4], [2, 5, 4], [5, 8, 4], [8, 7, 4], [7, 6, 4], [6, 3, 4], [3, 0, 4]];
  tri.forEach((t, i) => {
    ctx.beginPath(); t.forEach((k, j) => ctx[j ? 'lineTo' : 'moveTo'](pts[k][0], pts[k][1])); ctx.closePath();
    ctx.fillStyle = grey(0.62 + (i % 4) * 0.1 + R() * 0.03); ctx.fill();
    ctx.lineWidth = 8; ctx.strokeStyle = grey(1); ctx.stroke();
  });
  clouds(ctx, R, 10, 0.07, 80, 200);
  return canvas;
}
// POLISHED TEKTITE: natural glass ground mirror-smooth: the curved ripples of its fractures, and a soft sheen.
export function drawBuildTektite() {
  const { canvas, ctx } = canvasOf(0.8), R = rng(0xbd001c);
  clouds(ctx, R, 18, 0.1, 120, 300);
  for (let i = 0; i < 16; i++) {
    const x = R() * S, y = R() * S, r = 80 + R() * 240, a0 = R() * 6.28, a = 0.15 + R() * 0.25;
    for (const [dx, dy] of offsets(x - r, y - r, x + r, y + r)) {
      ctx.beginPath(); ctx.arc(x + dx, y + dy, r, a0, a0 + 1.4); ctx.lineWidth = 4 + R() * 4; ctx.strokeStyle = chalk(a); ctx.stroke();
    }
  }
  return canvas;
}

// ── FROM ANIMALS (the owner's Alien Animals items, Oct 29) ──────────────────────────────────────────────────────────────
// SHELLSTONE: pale pressed blocks, two courses a kube, a half-block apart, each ridged like a shell: a fan of lit and
// shaded ridges spreading from the middle of its foot, the way a scallop's do.
export function drawBuildShellstone() {
  const { canvas, ctx } = canvasOf(0.66), R = rng(0xbd001d), h = S / 2, w = S / 2, r = [];
  for (let row = 0; row < 2; row++) for (let i = 0; i < 2; i++) r.push([i * w + row * (w / 2), row * h, w, h]);
  blocks(ctx, r, R, { gap: 14, base: 0.92, vary: 0.04, bevel: 8 });
  const lit = [], dark = [];
  for (const [rx, ry, bw, bh] of r) {
    const fx = rx + bw / 2, fy = ry + bh - 20;                       // the fan's foot, the middle of the block's bottom
    for (let k = 0; k < 13; k++) {
      const a = Math.PI * (0.12 + 0.76 * k / 12), L = bh * 0.82;
      const x1 = fx + Math.cos(Math.PI - a) * L * 0.62, y1 = fy - Math.sin(a) * L;
      (k % 2 ? dark : lit).push([fx, fy, x1, y1]);
    }
  }
  strokes(ctx, lit, 14, chalk(0.16)); strokes(ctx, dark, 10, ink(0.12));
  for (const [rx, ry, bw, bh] of r) for (const k of [0.3, 0.55, 0.8]) {   // growth lines across the ridges
    const cx = mod(rx + bw / 2), cy = ry + bh - 20, rr = bh * 0.82 * k;
    for (const [dx, dy] of offsets(cx - rr, cy - rr, cx + rr, cy)) {
      ctx.beginPath(); ctx.ellipse(cx + dx, cy + dy, rr * 0.62, rr, 0, Math.PI, 2 * Math.PI); ctx.lineWidth = 4; ctx.strokeStyle = ink(0.08); ctx.stroke();
    }
  }
  specks(ctx, R, 500, 0.035);
  return canvas;
}
// SCALE TILES: rounded scales in rows, each row a half-scale across from the last and overlapping it, every scale lit on its
// rim and shaded at its root, with a faint sheen across some of them (the shimmer, with the material's own sheen).
export function drawBuildScaleTiles() {
  const { canvas, ctx } = canvasOf(0.55), R = rng(0xbd001e), n = 8, w = S / n, step = S / 8;
  for (let row = -1; row < 8; row++) for (let i = 0; i < n; i++) {
    const cx = i * w + (row & 1 ? w / 2 : 0), cy = row * step, v = 0.8 + R() * 0.12, sheen = R() < 0.35;
    for (const [dx, dy] of offsets(cx - w / 2, cy, cx + w / 2, cy + step * 1.5)) {
      const X = cx + dx, Y = cy + dy;
      ctx.beginPath(); ctx.moveTo(X - w / 2, Y); ctx.lineTo(X - w / 2, Y + step * 0.6);
      ctx.quadraticCurveTo(X - w / 2, Y + step * 1.5, X, Y + step * 1.5); ctx.quadraticCurveTo(X + w / 2, Y + step * 1.5, X + w / 2, Y + step * 0.6);
      ctx.lineTo(X + w / 2, Y); ctx.closePath();
      ctx.fillStyle = grey(v); ctx.fill();
      ctx.lineWidth = 8; ctx.strokeStyle = grey(0.42); ctx.stroke();
      ctx.fillStyle = ink(0.1); ctx.fillRect(X - w / 2 + 6, Y, w - 12, step * 0.25);
      if (sheen) { ctx.beginPath(); ctx.moveTo(X - w * 0.25, Y + step * 0.5); ctx.lineTo(X + w * 0.1, Y + step * 1.2); ctx.lineWidth = 10; ctx.strokeStyle = chalk(0.22); ctx.stroke(); }
    }
  }
  return canvas;
}

// The makers by style (building-kubes.mjs BUILDING_IDEAS' `style`, and the pillar's parts), for TextureEngine's
// 'build:<style>' slots.
export const BUILD_MAKERS = Object.freeze({
  chisel: drawBuildChisel, pillar: drawBuildPillar, pillarbase: drawBuildPillarBase, pillarshaft: drawBuildPillarShaft, pillarcap: drawBuildPillarCap,
  buffed: drawBuildBuffed, tiles: drawBuildTiles, dressed: drawBuildDressed,
  rammed: drawBuildRammed, sintered: drawBuildSintered, concrete: drawBuildConcrete,
  panels: drawBuildPanels, slats: drawBuildSlats,
  tread: drawBuildTread, grate: drawBuildGrate, corrugated: drawBuildCorrugated, hazard: drawBuildHazard, vent: drawBuildVent,
  icebrick: drawBuildIcebrick, snow: drawBuildSnow,
  lantern: drawBuildLantern, glowstrip: drawBuildGlowstrip, neonlamp: drawBuildNeonlamp, balloonlamp: drawBuildBalloonlamp,   // (the gas lamps, Oct 29: a lantern's frame, each its own slot)
  hull: drawBuildHull, honeycomb: drawBuildHoneycomb, corinlay: drawBuildCorinlay,
  crystal: drawBuildCrystal, tektite: drawBuildTektite,
  shellstone: drawBuildShellstone, scaletiles: drawBuildScaleTiles,   // (the animals' materials, Oct 29)
});
// What the Kubipedia calls each ("Texture: chiselled masonry").
export const BUILD_LABELS = Object.freeze({
  chisel: 'chiselled masonry', pillar: 'fluted column', pillarbase: 'fluted column', pillarshaft: 'fluted column', pillarcap: 'fluted column',
  buffed: 'buffed stone', tiles: 'large tiles', dressed: 'dressed stone', rammed: 'rammed earth', sintered: 'sintered nodules',
  concrete: 'cast concrete', panels: 'wood panels', slats: 'wood slats', tread: 'tread plate', grate: 'open grate',
  corrugated: 'corrugated sheet', hazard: 'hazard stripes', vent: 'vent grille', icebrick: 'ice bricks', snow: 'snow blocks',
  lantern: 'lantern', glowstrip: 'glow strips', neonlamp: 'neon lantern', balloonlamp: 'balloon lantern', hull: 'hull plates', honeycomb: 'honeycomb', corinlay: 'Cor inlay',
  crystal: 'cut facets', tektite: 'polished glass', shellstone: 'ridged shell masonry', scaletiles: 'overlapping scales',
});
